import { ARENA, CAMERA_STYLE, TICK_HZ, TILE, botInput, createInitialState, forfeitMatch, step, type Inputs, type Player, type PlayerId, type State } from '@chessshooter/shared';
import { fitWorld, followWorld, screenToWorld, type Camera } from '../camera';
import { BoardController } from '../chess/boardController';
import { resultDetail } from '../chess/status';
import { ChessAudio, ChessSoundEvents } from '../chess/sounds';
import { ArenaAudio } from '../arenaSounds';
import { createHud } from '../hud';
import { attachInput } from '../input';
import { createRenderer } from '../render';
import { loadSettings, saveSettings } from '../settings';
import { BOARD_STYLE, MAX_FRAME_MS } from '../uiConstants';
import type { ModeHandle } from './mode';

export interface LocalModeOptions {
  canvas: HTMLCanvasElement;
  hud: HTMLElement;
  seed: number;
}

const TICK_MS = 1000 / TICK_HZ;

/** Mirrors fire() in shared: a held weapon always fires. */
function canFire(p: Player): boolean {
  return p.weapon !== null;
}

/**
 * Plays against the bot in this tab: a fixed-step loop driven by requestAnimationFrame with an
 * accumulator, the same step() the server will run. Nothing here knows about the network. At the
 * board the pointers go to the move state machine instead of the pads, and a finished move rides on
 * the next tick's input like a shot does.
 */
export function startLocalMode(opts: LocalModeOptions): ModeHandle {
  const me: PlayerId = 0;
  const bot: PlayerId = 1;
  let state: State = createInitialState(ARENA, opts.seed);
  let resultShown = false;

  const settings = loadSettings();
  const audio = new ChessAudio(settings.chessSounds !== false);
  const arenaAudio = new ArenaAudio(settings.arenaSounds !== false);
  const soundEvents = new ChessSoundEvents();
  soundEvents.reset(state);
  arenaAudio.reset(state);
  const renderer = createRenderer(opts.canvas);
  const hud = createHud(opts.hud, { settings, onChange: () => { saveSettings(settings); audio.setEnabled(settings.chessSounds !== false); arenaAudio.setEnabled(settings.arenaSounds !== false); } });
  const input = attachInput(opts.canvas, settings);
  const { model } = input;
  const board = new BoardController(me, { setBoard: input.setBoard, showPromotion: hud.showPromotion, hidePromotion: hud.hidePromotion, playSelection: () => audio.play('select') }, BOARD_STYLE);
  hud.setSurrender(() => {
    if (state.winner !== null) return;
    state = forfeitMatch(state, me);
    board.reset();
    audio.stop();
    arenaAudio.stop();
    resultShown = true;
    hud.showResult(state.winner!, me, returnToMenu, 'Aufgegeben.');
  });

  let camera: Camera = fitWorld({ width: 1, height: 1 }, 1, 1);
  let dpr = 1;
  let topInset: number = CAMERA_STYLE.hudHeight;
  let viewport = { width: 1, height: 1 };
  function layout(): void {
    viewport = { width: window.innerWidth, height: window.innerHeight };
    dpr = Math.min(window.devicePixelRatio || 1, CAMERA_STYLE.maxDpr);
    topInset = opts.hud.querySelector('.hud-bar')?.getBoundingClientRect().bottom ?? CAMERA_STYLE.hudHeight;
    camera = followWorld(viewport, state.map.width * TILE, state.map.height * TILE, state.players[0].pos, topInset, settings.overview);
    renderer.resize(viewport, dpr);
  }
  window.addEventListener('resize', layout);
  layout();

  const toWorld = (p: { x: number; y: number }) => screenToWorld(camera, p);

  function returnToMenu(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('mode');
    window.location.assign(url);
  }

  let accumulator = 0;
  let last = performance.now();
  function frame(now: number): void {
    accumulator += Math.min(now - last, MAX_FRAME_MS);
    last = now;
    while (accumulator >= TICK_MS) {
      const mine = model.take(state.tick, now, state.players[me].pos, toWorld);
      if (state.phase === 'chess') {
        const move = board.take(state);
        if (move) mine.chessMove = move;
      } else if (mine.fireAt && !canFire(state.players[me])) {
        hud.flashSlot();
      }
      const previousPhase = state.phase;
      state = step(state, { [me]: mine, [bot]: botInput(state, bot) } as Inputs);
      if (state.phase !== previousPhase) audio.stop();
      arenaAudio.update(state, me);
      const sound = soundEvents.update(state);
      if (sound) audio.play(sound);
      accumulator -= TICK_MS;
    }
    const scene = board.sync(state, viewport, topInset);
    opts.canvas.classList.toggle('board', scene !== null);
    const touch = model.touch();
    camera = followWorld(viewport, state.map.width * TILE, state.map.height * TILE, state.players[me].pos, topInset, settings.overview);
    renderer.draw(state, { me, camera, touch: touch.touched ? touch : null, cursor: model.cursor(), aimLine: settings.aimLine, dpr, viewport, topInset, board: scene });
    hud.update(state, me, scene ? { layout: scene.layout, check: scene.position.check } : null);
    if (state.winner !== null && !resultShown) {
      resultShown = true;
      hud.showResult(state.winner, me, returnToMenu, resultDetail(state.outcome, state.winner, me));
    }
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) last = performance.now();
  });

  return { state: () => state, touch: () => model.touch(), board: () => board.layout };
}
